import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ClipboardCheck, Loader2, Building2, User, Users, Plus, ChevronRight, ArrowLeft, Shield, UserCog, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [step, setStep] = useState(1); // 1: Choose type, 2: Details
  const [accountType, setAccountType] = useState(''); // 'newCompany', 'joinCompany', 'individual'
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('user'); // 'manager' or 'user' when joining
  
  // Company fields
  const [siteName, setSiteName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  
  // Available data
  const [companies, setCompanies] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Load companies on mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoadingData(true);
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies');
      setCompanies([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleAccountTypeSelect = (type) => {
    setAccountType(type);
    setStep(2);
    // Reset fields
    setRole(type === 'joinCompany' ? 'user' : '');
    setSiteName('');
    setCompanyName('');
    setSelectedCompanyId('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validation based on account type
    if (accountType === 'newCompany') {
      if (!companyName.trim()) {
        toast.error('Please enter a company name');
        return;
      }
      if (!siteName.trim()) {
        toast.error('Please enter a construction site name');
        return;
      }
    } else if (accountType === 'joinCompany') {
      if (!selectedCompanyId) {
        toast.error('Please select a company to join');
        return;
      }
      if (!role) {
        toast.error('Please select your role');
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      let registrationData = {
        name,
        email,
        password,
        role: accountType === 'newCompany' ? 'admin' : role,
        is_individual: accountType === 'individual',
        site_name: siteName,
        company_name: accountType === 'newCompany' ? companyName : null,
        company_id: accountType === 'joinCompany' ? selectedCompanyId : null,
      };
      
      await register(
        registrationData.name,
        registrationData.email,
        registrationData.password,
        registrationData.role,
        registrationData.site_name,
        '',
        registrationData.is_individual,
        registrationData.company_id,
        registrationData.company_name
      );
      
      if (accountType === 'joinCompany' && role === 'manager') {
        toast.success('Account created! Your manager access is pending approval from the company admin.');
      } else {
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-5">
      {/* Pricing Info Box */}
      <div className="p-4 bg-gradient-to-r from-primary/5 via-slate-50 to-orange-50 rounded-lg border border-slate-200" data-testid="pricing-box">
        <div className="text-center mb-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            7-Day Free Trial
          </span>
        </div>
        <div className="flex justify-center items-center gap-6">
          <div className="text-center">
            <span className="text-2xl font-bold text-slate-800">£3</span>
            <span className="text-xs text-slate-500 block font-medium">per month</span>
          </div>
          <div className="text-slate-300 font-light">or</div>
          <div className="text-center">
            <span className="text-2xl font-bold text-orange-500">£10</span>
            <span className="text-xs text-slate-500 block font-medium">lifetime</span>
          </div>
        </div>
      </div>
      
      <div className="grid gap-3">
        {/* Create New Company - becomes Admin */}
        <div 
          className="group cursor-pointer p-4 rounded-lg border-2 border-slate-200 bg-white hover:border-primary hover:shadow-md transition-all duration-200"
          onClick={() => handleAccountTypeSelect('newCompany')}
          data-testid="new-company-option"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-105 transition-all duration-200">
              <Shield className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-800 group-hover:text-primary transition-colors">Register a New Company</h3>
              <p className="text-sm text-slate-500">
                You'll become the <span className="font-semibold text-primary">Admin</span> with full control
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </div>
        </div>
        
        {/* Join Existing Company */}
        <div 
          className="group cursor-pointer p-4 rounded-lg border-2 border-slate-200 bg-white hover:border-primary hover:shadow-md transition-all duration-200"
          onClick={() => handleAccountTypeSelect('joinCompany')}
          data-testid="join-company-option"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-500 group-hover:scale-105 transition-all duration-200">
              <Building2 className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">Join Existing Company</h3>
              <p className="text-sm text-slate-500">
                Join as a <span className="font-semibold">Manager</span> or <span className="font-semibold">Operative</span>
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
        
        {/* Individual Operative */}
        <div 
          className="group cursor-pointer p-4 rounded-lg border-2 border-slate-200 bg-white hover:border-primary hover:shadow-md transition-all duration-200"
          onClick={() => handleAccountTypeSelect('individual')}
          data-testid="individual-option"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-orange-500 group-hover:scale-105 transition-all duration-200">
              <User className="h-6 w-6 text-orange-600 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-800 group-hover:text-orange-600 transition-colors">Individual Operative</h3>
              <p className="text-sm text-slate-500">
                Work independently, see only your own forms
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </div>
      
      {/* Terms Link */}
      <div className="text-center pt-2">
        <Link to="/terms" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-primary transition-colors">
          <FileText className="h-3.5 w-3.5" />
          Terms & Conditions
        </Link>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setStep(1)}
        className="mb-2 -ml-2"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back
      </Button>
      
      {/* Account Type Badge */}
      <div className="mb-4 p-3 bg-slate-50 rounded-lg border">
        {accountType === 'newCompany' && (
          <div className="flex items-center gap-2 text-primary">
            <Shield className="h-5 w-5" />
            <span className="font-medium">Registering as Company Admin</span>
          </div>
        )}
        {accountType === 'joinCompany' && (
          <div className="flex items-center gap-2 text-blue-600">
            <Building2 className="h-5 w-5" />
            <span className="font-medium">Joining Existing Company</span>
          </div>
        )}
        {accountType === 'individual' && (
          <div className="flex items-center gap-2 text-orange-600">
            <User className="h-5 w-5" />
            <span className="font-medium">Individual Account</span>
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="name">Full Name</Label>
        <Input
          id="name"
          type="text"
          placeholder="John Smith"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          data-testid="register-name-input"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="register-email-input"
        />
      </div>
      
      {/* New Company Fields */}
      {accountType === 'newCompany' && (
        <div className="pt-4 border-t border-slate-200 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name *</Label>
            <Input
              id="companyName"
              placeholder="e.g., ABC Construction Ltd"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              data-testid="company-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="siteName">Construction Site Name *</Label>
            <Input
              id="siteName"
              placeholder="e.g., London Bridge Project"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              required
              data-testid="site-name-input"
            />
            <p className="text-xs text-muted-foreground">
              You can add more sites later from your profile
            </p>
          </div>
        </div>
      )}
      
      {/* Join Company Fields */}
      {accountType === 'joinCompany' && (
        <div className="pt-4 border-t border-slate-200 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role">Your Role *</Label>
            <Select value={role} onValueChange={setRole} required>
              <SelectTrigger data-testid="register-role-select">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>Operative (Trade Worker)</span>
                  </div>
                </SelectItem>
                <SelectItem value="manager">
                  <div className="flex items-center gap-2">
                    <UserCog className="h-4 w-4" />
                    <span>Manager (Site Manager)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {role === 'manager' && (
              <p className="text-xs text-amber-600">
                Manager access requires approval from the company admin
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Select Company *</Label>
            {loadingData ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading companies...
              </div>
            ) : companies.length > 0 ? (
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger data-testid="company-select">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.company_id} value={company.company_id}>
                      <div>
                        <span className="font-medium">{company.name}</span>
                        {company.site_name && (
                          <span className="text-muted-foreground ml-2">({company.site_name})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                No companies registered yet. Would you like to <button 
                  type="button"
                  className="text-primary underline font-medium"
                  onClick={() => handleAccountTypeSelect('newCompany')}
                >
                  register a new company
                </button> instead?
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Individual notice */}
      {accountType === 'individual' && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <User className="h-4 w-4 inline mr-2" />
          As an individual, you'll only see forms you create. You can join a company later.
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="At least 6 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          data-testid="register-password-input"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="Confirm your password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          data-testid="register-confirm-password-input"
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-primary hover:bg-primary/90"
        disabled={isLoading || (accountType === 'joinCompany' && !selectedCompanyId)}
        data-testid="register-submit-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating account...
          </>
        ) : (
          'Create Account'
        )}
      </Button>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4 shadow-lg">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-800 tracking-tight">QA BUILDER PRO</h1>
          <p className="text-slate-500 text-sm mt-1">Construction Quality Assurance</p>
        </div>

        <Card className="border-slate-200 shadow-xl bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl tracking-tight">
              {step === 1 ? 'GET STARTED' : 'CREATE ACCOUNT'}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {step === 1 
                ? 'Choose how you want to use the app' 
                : accountType === 'newCompany'
                  ? 'Set up your company and admin account'
                  : accountType === 'joinCompany'
                    ? 'Join your team'
                    : 'Create your individual account'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? renderStep1() : renderStep2()}
            
            <div className="mt-6 pt-6 border-t border-slate-100 text-center text-sm">
              <span className="text-slate-500">Already have an account? </span>
              <Link 
                to="/login" 
                className="text-primary hover:text-primary/80 font-semibold transition-colors"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
        
        {/* Footer Links */}
        <div className="mt-6 text-center text-xs text-slate-400 space-x-3">
          <Link to="/terms" className="hover:text-slate-600 transition-colors">Terms</Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-slate-600 transition-colors">Privacy</Link>
          <span>•</span>
          <Link to="/pricing" className="hover:text-slate-600 transition-colors">Pricing</Link>
        </div>
        
        <p className="text-center text-xs text-slate-400 mt-4">© 2025 QA Builder Pro</p>
      </div>
    </div>
  );
}
